import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import posthog from 'posthog-js';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Zap, FileText, Trash2, CheckCircle2, Clock, Edit, Download,
  Copy, AlertTriangle, Briefcase, Home, DollarSign, Landmark, ChevronDown, Sparkles, Link as LinkIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_META = {
  job_application: { icon: Briefcase, blurb: 'Resume-driven job apps' },
  rental_application: { icon: Home, blurb: 'Apartment / lease forms' },
  loan_application: { icon: DollarSign, blurb: 'Personal & auto loans' },
  government_form: { icon: Landmark, blurb: 'ID / civic forms' },
};

export default function Applications() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [autofilling, setAutofilling] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('job_application');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [previewApp, setPreviewApp] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [coachApp, setCoachApp] = useState(null);
  const [coachData, setCoachData] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [appsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/applications`, { withCredentials: true }),
        axios.get(`${API}/applications/templates`, { withCredentials: true }),
      ]);
      setApplications(appsRes.data);
      setTemplates(templatesRes.data);
    } catch (e) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createFromTemplate = async (tmpl) => {
    setCreating(true);
    try {
      await axios.post(`${API}/applications/create`, {
        app_type: tmpl,
        title: templates[tmpl]?.title || 'Application',
      }, { withCredentials: true });
      toast.success('Application created');
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const handleAutofill = async (id, openPreview = true) => {
    setAutofilling((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/applications/${id}/autofill`, {}, { withCredentials: true });
      toast.success(`Filled ${data.filled_fields}/${data.total_fields} fields (${data.completion_rate}%)`);
      try { posthog.capture('application_autofilled', { app_id: id, filled_fields: data.filled_fields, total_fields: data.total_fields, completion_rate: data.completion_rate }); } catch (e) {}
      await fetchData();
      if (openPreview) {
        const refreshed = await axios.get(`${API}/applications/${id}`, { withCredentials: true });
        setPreviewApp(refreshed.data);
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Autofill limit reached — redirecting to upgrade page…');
        setTimeout(() => { window.location.href = '/upgrade'; }, 1200);
      } else toast.error('Failed to autofill');
    } finally {
      setAutofilling((p) => ({ ...p, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/applications/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await axios.put(`${API}/applications/${id}`, { form_data: formData }, { withCredentials: true });
      toast.success('Saved');
      setEditApp(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) { toast.error('Paste a URL first'); return; }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/import/url`, { url: importUrl.trim() }, { withCredentials: true });
      setImportResult(data);
      toast.success(`Extracted ${data.fields_extracted} fields`);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('URL Importer is Pro'); window.location.href = '/upgrade'; }
      else toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCoach = async (app) => {
    setCoachApp(app);
    setCoachData(null);
    setCoachLoading(true);
    try {
      const { data } = await axios.post(`${API}/coach/review/${app.application_id}`, {}, { withCredentials: true });
      setCoachData(data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('AI Coach is Pro'); window.location.href = '/upgrade'; setCoachApp(null); }
      else { toast.error('Review failed'); setCoachApp(null); }
    } finally {
      setCoachLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="applications-loading">
        <p className="text-sm uppercase tracking-widest text-muted-foreground">LOADING...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 md:px-8 py-8 max-w-5xl" data-testid="applications-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl mb-1">APPLICATIONS</h1>
          <p className="text-muted-foreground text-sm">Pick a template. Click autofill. Export or copy.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="uppercase text-xs tracking-wider" data-testid="import-url-btn">
            <LinkIcon className="w-4 h-4 mr-2" /> Import URL
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="new-application-btn">
              <Plus className="w-4 h-4 mr-2" /> New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider">PICK A TEMPLATE</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {Object.entries(templates).map(([key, tmpl]) => {
                const meta = TEMPLATE_META[key] || {};
                const Icon = meta.icon || FileText;
                return (
                  <button
                    key={key}
                    onClick={() => createFromTemplate(key)}
                    disabled={creating}
                    className="text-left border border-border p-4 hover:border-primary hover:bg-secondary/30 transition-all group"
                    data-testid={`template-card-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 border border-border flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading text-sm mb-0.5">{tmpl.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                          {meta.blurb || `${tmpl.fields.length} fields`}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {tmpl.fields.length} fields · {tmpl.fields.slice(0, 3).map((f) => f.replace(/_/g, ' ')).join(' · ')}…
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center" data-testid="no-applications">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-heading uppercase text-sm tracking-wider mb-1">NO APPLICATIONS YET</h3>
          <p className="text-xs text-muted-foreground mb-4">Create your first application to start autofilling.</p>
          <Button onClick={() => setDialogOpen(true)} className="brutalist-shadow-sm uppercase text-xs tracking-wider">
            <Plus className="w-4 h-4 mr-2" /> Create Application
          </Button>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="applications-list">
          {applications.map((app) => (
            <ApplicationCard
              key={app.application_id}
              app={app}
              templates={templates}
              autofilling={autofilling[app.application_id]}
              onAutofill={() => handleAutofill(app.application_id, true)}
              onPreview={() => setPreviewApp(app)}
              onEdit={() => setEditApp(app)}
              onDelete={() => handleDelete(app.application_id)}
              onCoach={() => handleCoach(app)}
            />
          ))}
        </div>
      )}

      {editApp && (
        <EditDialog
          app={editApp}
          templates={templates}
          onClose={() => setEditApp(null)}
          onSave={(formData) => handleUpdate(editApp.application_id, formData)}
        />
      )}

      {previewApp && (
        <PreviewDialog
          app={previewApp}
          templates={templates}
          onClose={() => setPreviewApp(null)}
          onEdit={() => { setEditApp(previewApp); setPreviewApp(null); }}
        />
      )}

      {/* Import URL Dialog */}
      <Dialog open={importOpen} onOpenChange={(open) => { setImportOpen(open); if (!open) { setImportUrl(''); setImportResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2">
              <LinkIcon className="w-5 h-5" /> IMPORT FROM URL
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Paste a URL to an online form or PDF. AI scans it, matches fields to your profile, returns filled data.
              {user?.subscription_tier !== 'pro' && <span className="text-primary ml-1">Pro feature.</span>}
            </p>
            {!importResult ? (
              <>
                <Input
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://..."
                  className="font-mono"
                  data-testid="import-url-input"
                />
                <Button onClick={handleImport} disabled={importing} className="w-full brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="import-submit-btn">
                  {importing ? 'IMPORTING...' : 'EXTRACT & FILL'}
                </Button>
              </>
            ) : (
              <div className="space-y-3" data-testid="import-result">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">
                  {importResult.fields_extracted} FIELDS EXTRACTED
                </div>
                <pre className="bg-secondary/40 border border-border p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-words max-h-60">
                  {JSON.stringify(importResult.form_data, null, 2)}
                </pre>
                <Button
                  onClick={async () => { try { await navigator.clipboard.writeText(JSON.stringify(importResult.form_data, null, 2)); toast.success('Copied'); } catch { toast.error('Copy failed'); } }}
                  variant="outline"
                  className="w-full uppercase text-xs tracking-wider"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" /> COPY ALL AS JSON
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Coach Dialog */}
      <Dialog open={!!coachApp} onOpenChange={() => { setCoachApp(null); setCoachData(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wider flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" /> AI COACH REVIEW
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2" data-testid="coach-dialog">
            {coachLoading ? (
              <div className="py-8 flex items-center justify-center">
                <Clock className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : coachData ? (
              <>
                <div className="flex items-baseline gap-3">
                  <div className="font-heading text-4xl">{coachData.score || 0}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 100 READINESS</div>
                </div>
                {coachData.summary && <p className="text-sm italic border-l-2 border-primary pl-3">{coachData.summary}</p>}
                {coachData.issues?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 text-yellow-600" /> ISSUES
                    </div>
                    <ul className="space-y-1.5">
                      {coachData.issues.map((i, idx) => (
                        <li key={idx} className="text-xs text-muted-foreground border-l-2 border-yellow-500 pl-2">{i}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {coachData.improvements?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-primary" /> IMPROVEMENTS
                    </div>
                    <ul className="space-y-1.5">
                      {coachData.improvements.map((i, idx) => (
                        <li key={idx} className="text-xs text-muted-foreground border-l-2 border-primary pl-2">{i}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApplicationCard({ app, templates, autofilling, onAutofill, onPreview, onEdit, onDelete, onCoach }) {
  const completion = app.total_fields > 0 ? Math.round((app.filled_fields / app.total_fields) * 100) : 0;
  const meta = TEMPLATE_META[app.app_type] || {};
  const Icon = meta.icon || FileText;
  const statusColor = {
    draft: 'text-muted-foreground',
    filled: 'text-primary',
    submitted: 'text-accent',
  }[app.status] || 'text-muted-foreground';

  return (
    <div className="border border-border bg-background p-4 hover:bg-secondary/20 transition-colors" data-testid={`app-${app.application_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 border border-border flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h3 className="font-heading text-sm">{app.title}</h3>
              <div className="flex items-center gap-2 mt-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                <span className={statusColor}>
                  {app.status === 'submitted' && <CheckCircle2 className="w-3 h-3 inline mr-1" />}
                  {app.status}
                </span>
                <span>·</span>
                <span>{app.filled_fields}/{app.total_fields} FIELDS</span>
                <span>·</span>
                <span>{new Date(app.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {app.filled_fields > 0 && (
                <>
                  <Button size="sm" variant="ghost" onClick={onPreview} className="uppercase text-xs tracking-wider" data-testid={`preview-btn-${app.application_id}`}>
                    Preview
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onCoach} className="uppercase text-xs tracking-wider" data-testid={`coach-btn-${app.application_id}`} title="AI Review">
                    <Sparkles className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {app.status !== 'submitted' && (
                <Button size="sm" onClick={onAutofill} disabled={autofilling} className="uppercase text-xs tracking-wider brutalist-shadow-sm" data-testid={`autofill-btn-${app.application_id}`}>
                  {autofilling ? (<><Clock className="w-3 h-3 mr-1 animate-spin" /> FILLING</>) : (<><Zap className="w-3 h-3 mr-1" /> AUTOFILL</>)}
                </Button>
              )}
              <Button size="icon" variant="ghost" onClick={onEdit} data-testid={`edit-btn-${app.application_id}`}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button size="icon" variant="ghost" onClick={onDelete} className="text-destructive hover:text-destructive" data-testid={`delete-btn-${app.application_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-[10px] uppercase tracking-widest mb-1">
              <span className="text-muted-foreground">COMPLETION</span>
              <span className="font-mono">{completion}%</span>
            </div>
            <div className="h-1 bg-secondary">
              <div className={`h-full transition-all duration-500 ${completion === 100 ? 'bg-accent' : 'bg-primary'}`} style={{ width: `${completion}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewDialog({ app, templates, onClose, onEdit }) {
  const template = templates[app.app_type];
  const fields = template?.fields || Object.keys(app.form_data || {});
  const filled = fields.filter((f) => {
    const v = app.form_data?.[f];
    return v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0) && v !== 0;
  });
  const missing = fields.filter((f) => !filled.includes(f));

  const formatValue = (v) => {
    if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
    if (typeof v === 'object' && v !== null) return JSON.stringify(v);
    return v === '' || v === null || v === undefined ? '—' : String(v);
  };

  const copyAll = async () => {
    const text = fields.map((f) => `${f}: ${formatValue(app.form_data?.[f])}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const exportFile = async (format) => {
    try {
      if (format === 'html') {
        // Open in new tab so user can print to PDF
        const token = localStorage.getItem('auth_token');
        const url = `${API}/applications/${app.application_id}/export?format=html&auth=${encodeURIComponent(token || '')}`;
        window.open(url, '_blank');
        return;
      }
      const res = await axios.get(`${API}/applications/${app.application_id}/export?format=${format}`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(app.title || 'application').replace(/[^a-z0-9_-]/gi, '_')}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider flex items-center justify-between gap-3">
            <span className="truncate">{app.title}</span>
            <span className="text-xs font-mono text-muted-foreground flex-shrink-0">
              {filled.length}/{fields.length} FILLED
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 pb-4 border-b border-border">
          <Button size="sm" variant="outline" onClick={copyAll} className="uppercase text-xs tracking-wider" data-testid="copy-all-btn">
            <Copy className="w-3.5 h-3.5 mr-2" /> Copy All
          </Button>
          <Button size="sm" variant="outline" onClick={() => {
            const w = window.open(`/fill?app=${app.application_id}`, 'lifeAdminFill', 'width=420,height=760,top=100,left=100,resizable=yes,scrollbars=yes');
            if (w) w.focus();
          }} className="uppercase text-xs tracking-wider" data-testid="open-fill-helper-btn">
            <Zap className="w-3.5 h-3.5 mr-2" /> Open Fill Helper
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="uppercase text-xs tracking-wider" data-testid="export-btn">
                <Download className="w-3.5 h-3.5 mr-2" /> Export <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportFile('json')} data-testid="export-json">JSON</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportFile('csv')} data-testid="export-csv">CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportFile('html')} data-testid="export-pdf">Printable / PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" variant="ghost" onClick={onEdit} className="uppercase text-xs tracking-wider ml-auto" data-testid="edit-from-preview-btn">
            <Edit className="w-3.5 h-3.5 mr-2" /> Edit
          </Button>
        </div>

        {/* Missing fields alert */}
        {missing.length > 0 && (
          <div className="border border-border bg-secondary/40 p-3 flex items-start gap-2" data-testid="missing-fields-alert">
            <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="uppercase tracking-widest text-muted-foreground mb-1">
                {missing.length} missing — Strengthen your profile to fill these next time
              </div>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((f) => (
                  <span key={f} className="px-1.5 py-0.5 bg-background border border-border font-mono text-[10px]">
                    {f.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Field list */}
        <div className="space-y-1" data-testid="preview-fields">
          {fields.map((f) => {
            const v = app.form_data?.[f];
            const isMissing = missing.includes(f);
            const displayValue = formatValue(v);
            const copyField = async () => {
              if (isMissing) return;
              try {
                await navigator.clipboard.writeText(displayValue);
                toast.success(`Copied "${f.replace(/_/g, ' ')}"`);
              } catch { toast.error('Copy failed'); }
            };
            return (
              <button
                key={f}
                onClick={copyField}
                disabled={isMissing}
                className={`w-full text-left grid grid-cols-3 gap-3 py-2 border-b border-border/60 text-xs transition-colors ${isMissing ? 'opacity-40 cursor-not-allowed' : 'hover:bg-secondary/30 cursor-pointer'}`}
                data-testid={`copy-field-${f}`}
                title={isMissing ? 'Missing — no value to copy' : 'Click to copy'}
              >
                <div className="uppercase tracking-widest text-muted-foreground col-span-1 flex items-center gap-2">
                  <Copy className="w-3 h-3 opacity-50" />
                  {f.replace(/_/g, ' ')}
                </div>
                <div className="font-mono col-span-2 break-words whitespace-pre-wrap">
                  {displayValue}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ app, templates, onClose, onSave }) {
  const [formData, setFormData] = useState(app.form_data || {});
  const [saving, setSaving] = useState(false);
  const template = templates[app.app_type];

  const handleSave = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">{app.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2" data-testid="edit-form">
          {template?.fields?.map((field) => (
            <div key={field}>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">
                {field.replace(/_/g, ' ')}
              </Label>
              <Input
                value={Array.isArray(formData[field]) ? formData[field].join(', ') : formData[field] || ''}
                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                className="font-mono"
                data-testid={`field-${field}`}
              />
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1 brutalist-shadow-sm uppercase text-xs tracking-wider" data-testid="save-form-btn">
              {saving ? 'SAVING...' : 'SAVE CHANGES'}
            </Button>
            <Button variant="outline" onClick={onClose} className="uppercase text-xs tracking-wider">CANCEL</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
